import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';

import type { GroupSeatData, GroupSubscriptionData } from '../groupSubscriptionTypes';
import { BulkActionBar } from './BulkActionBar';
import { ContactPublisherNote } from './ContactPublisherNote';
import { GroupCardHeader } from './GroupCardHeader';
import { FILTER_MATCHERS, FILTER_TABS, type FilterKey } from './groupCard.utils';
import type { GroupConfirmKind } from './groupConfirmCopy';
import { ManageSeatsLink } from './ManageSeatsLink';
import { MemberRow } from './MemberRow';
import { NoSeatsEmptyState } from './NoSeatsEmptyState';
import { OffPlatformBillingBanner } from './OffPlatformBillingBanner';
import { PastDueBanner } from './PastDueBanner';
import { PaymentPendingBanner } from './PaymentPendingBanner';
import { PrePaymentGroupCard } from './PrePaymentGroupCard';
import { ScheduledGroupCard } from './ScheduledGroupCard';
import { ScheduledReductionBanner } from './ScheduledReductionBanner';
import { SeatPagination, type SeatPaginationMeta } from './SeatPagination';
import { SeatSearchInput } from './SeatSearchInput';
import { seatsRemaining as computeSeatsRemaining } from './seatMath';
import { useGroupSelection } from './useGroupSelection';

// Lead email for confirm copy ("Remove foo@bar.com and 4 others?").
function firstEmailOf(ids: string[], pool: GroupSeatData[]): string | null {
  const id = ids[0];
  if (!id) return null;
  return pool.find((s) => s.id === id)?.email ?? null;
}

type GroupCardProps = {
  group: GroupSubscriptionData;
  /** Single-expansion accordion: parent toggles to close any other open card. */
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onOpenManageSeats?: () => void;
  onOpenInviteByEmail?: () => void;
  onOpenBulkInviteCsv?: () => void;
  /** `firstEmail` populates "Remove foo@bar.com?" copy without a host lookup. */
  onOpenConfirm?: (kind: GroupConfirmKind, seatIds: string[], firstEmail?: string | null) => void;
  onViewBilling?: () => void;
  onCancelSubscription?: () => void;
  onRestoreSubscription?: () => void;
  onRetryFailedPayment?: () => void;
  /** Host action URL the past-due CTA posts to (native form submit → host mints
   *  a fresh Stripe billing-portal session and 302s to it). Optional — without it
   *  the past-due banner still renders but drops the CTA. */
  updatePaymentMethodAction?: string;
  /** Opens the picker to swap which members are scheduled to lose access at
   *  renewal. Shared by the banner "Modify" link and each terminating row's
   *  "Manage" action. */
  onModifyReduction?: () => void;
  /** Cancels the scheduled reduction entirely (banner "Cancel reduction"). */
  onCancelReduction?: () => void;
  translations?: Record<string, string>;
  /**
   * Controlled mode (live host): pass `seats` + `onFilterChange` etc. and the
   * card stops running client-side filters — the host supplies a pre-filtered
   * slice. Omit them all for Dream Builder preview where `group.seats` IS the
   * full universe.
   */
  seats?: GroupSeatData[];
  pagination?: SeatPaginationMeta | null;
  isLoadingSeats?: boolean;
  activeFilter?: FilterKey;
  searchQuery?: string;
  onFilterChange?: (key: FilterKey) => void;
  onSearchChange?: (query: string) => void;
  onPageChange?: (page: number) => void;
};

export function GroupCard({
  group,
  isExpanded,
  onToggleExpanded,
  onOpenManageSeats,
  onOpenInviteByEmail,
  onOpenBulkInviteCsv,
  onOpenConfirm,
  onViewBilling,
  onCancelSubscription,
  onRestoreSubscription,
  onRetryFailedPayment,
  updatePaymentMethodAction,
  onModifyReduction,
  onCancelReduction,
  translations,
  seats: controlledSeats,
  pagination,
  isLoadingSeats = false,
  activeFilter: controlledFilter,
  searchQuery: controlledSearch,
  onFilterChange,
  onSearchChange,
  onPageChange,
}: GroupCardProps) {
  const isControlled = onFilterChange !== undefined;

  const [internalFilter, setInternalFilter] = useState<FilterKey>('all');
  const [internalSearch, setInternalSearch] = useState('');

  const activeFilter = isControlled ? (controlledFilter ?? 'all') : internalFilter;
  const searchQuery = isControlled ? (controlledSearch ?? '') : internalSearch;

  const visibleSeats = useMemo(() => {
    if (isControlled) return controlledSeats ?? [];
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const matchesQuery = (seat: { email: string | null }) =>
      normalizedQuery === '' || (seat.email?.toLowerCase().includes(normalizedQuery) ?? false);
    return group.seats.filter((seat) => FILTER_MATCHERS[activeFilter](seat) && matchesQuery(seat));
  }, [isControlled, controlledSeats, group.seats, activeFilter, searchQuery]);

  // In controlled mode selection is page-scoped — we only have the current page.
  const selectionSource = isControlled ? visibleSeats : group.seats;
  const {
    selectedIds,
    hasSelection,
    allVisibleSelected,
    hasActionableVisible,
    headerCheckboxRef,
    toggleSeat,
    toggleAllVisible,
    clearSelection,
    selectedSeats,
  } = useGroupSelection(selectionSource, visibleSeats);

  // Clear selection when host swaps the seats page — prior selection is off-screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: controlledSeats reference change IS the trigger.
  useEffect(() => {
    if (isControlled) clearSelection();
  }, [isControlled, controlledSeats, clearSelection]);

  // Filter/search change can hide selected rows — clear so bulk bar doesn't show invisible counts.
  const setFilter = useCallback(
    (key: FilterKey) => {
      if (isControlled) {
        onFilterChange?.(key);
      } else {
        setInternalFilter(key);
        clearSelection();
      }
    },
    [isControlled, onFilterChange, clearSelection],
  );

  const handleSearchChange = useCallback(
    (next: string) => {
      if (isControlled) {
        onSearchChange?.(next);
      } else {
        setInternalSearch(next);
        clearSelection();
      }
    },
    [isControlled, onSearchChange, clearSelection],
  );

  const isSearching = searchQuery.trim() !== '';

  if (
    group.status === 'payment_pending' ||
    group.status === 'awaiting_payment' ||
    group.status === 'pre_approved' ||
    group.status === 'payment_failed'
  ) {
    return (
      <PrePaymentGroupCard group={group} onRetryFailedPayment={onRetryFailedPayment} translations={translations} />
    );
  }

  if (group.status === 'scheduled') {
    return <ScheduledGroupCard group={group} translations={translations} />;
  }

  // "Seats remaining" subtracts filled + terminating + invites_pending because
  // pending invites RESERVE a seat — see seatMath.ts for why.
  const remaining = computeSeatsRemaining(group);
  const percentFilled = group.max_seats > 0 ? Math.min((group.seats_filled / group.max_seats) * 100, 100) : 0;
  // In controlled mode `group.seats` is empty until the per-card loader resolves;
  // trust the index counts so we don't flash the empty state.
  const hasNoSeats = isControlled ? group.seats_filled === 0 && group.invites_pending === 0 : group.seats.length === 0;
  const isOffPlatform = group.payment_collection_method === 'off_platform';
  const seatAdditionPending = group.pending_seat_addition != null;

  // Seat COUNT is billed externally for any custom deal (off-platform or
  // send-invoice), so count-changing actions (scheduled-reduction modify/cancel,
  // terminating-row manage) are publisher-only.
  const seatCountLocked = group.custom_deal;

  // Off-platform still surfaces "Manage seats" — the click opens a host-routed
  // modal telling the admin to contact the publisher (they still can't change the
  // count). Only send-invoice custom deals hide the entry point outright and show
  // a ContactPublisherNote in its place.
  const inlineSeatManagementHidden = group.custom_deal && !isOffPlatform;
  const manageSeats = seatAdditionPending || inlineSeatManagementHidden ? undefined : onOpenManageSeats;
  const manageSeatsLabel = manageSeats
    ? (translations?.['group_subscriptions.card.manage_seats'] ?? 'Manage seats')
    : undefined;
  let seatActionSlot: ReactNode | null = null;
  if (manageSeats) {
    seatActionSlot = (
      <span className="ml-1.5 hidden lg:inline">
        <ManageSeatsLink onClick={manageSeats} label={manageSeatsLabel} />
      </span>
    );
  } else if (inlineSeatManagementHidden) {
    seatActionSlot = (
      <span className="ml-1.5 hidden lg:inline">
        <ContactPublisherNote email={group.publication_contact_email} translations={translations} />
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 bg-white border border-gray-200 rounded-md">
      <GroupCardHeader
        group={group}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
        hideToggle={hasNoSeats}
        hideInviteMenu={hasNoSeats}
        onManageSeats={manageSeats ? () => manageSeats() : undefined}
        onInviteByEmail={onOpenInviteByEmail ? () => onOpenInviteByEmail() : undefined}
        onBulkInviteCsv={onOpenBulkInviteCsv ? () => onOpenBulkInviteCsv() : undefined}
        onViewBilling={onViewBilling ? () => onViewBilling() : undefined}
        onCancelSubscription={onCancelSubscription ? () => onCancelSubscription() : undefined}
        onRestoreSubscription={onRestoreSubscription ? () => onRestoreSubscription() : undefined}
        translations={translations}
      />

      <OffPlatformBillingBanner group={group} translations={translations} />
      <PastDueBanner group={group} updatePaymentMethodAction={isOffPlatform ? undefined : updatePaymentMethodAction} />

      <PaymentPendingBanner group={group} />

      {/* Seat reductions change the seat COUNT, so their modify/cancel actions are
          publisher-only for locked (custom-deal / off-platform) groups — the banner
          still shows, it just loses the admin-facing buttons. */}
      <ScheduledReductionBanner
        group={group}
        onModify={seatCountLocked ? undefined : onModifyReduction}
        onCancel={seatCountLocked ? undefined : onCancelReduction}
      />

      {hasNoSeats && (
        <NoSeatsEmptyState
          remaining={remaining}
          translations={translations}
          onInviteByEmail={onOpenInviteByEmail}
          onBulkInviteCsv={onOpenBulkInviteCsv}
        />
      )}

      {!hasNoSeats && !isExpanded && (
        <div className="flex items-center justify-between gap-4 text-xs">
          <span className="font-medium text-gray-900 whitespace-nowrap">
            {remaining} {remaining === 1 ? 'seat' : 'seats'} remaining
            {seatActionSlot}
          </span>
          <span className="text-right text-gray-500 whitespace-nowrap">
            {group.seats_filled} of {group.max_seats} filled
            {group.invites_pending > 0 && ` (${group.invites_pending} pending)`}
          </span>
        </div>
      )}

      {!hasNoSeats && isExpanded && (
        <>
          {/* The full set of pills needs most of a narrow card to itself, so tabs
              stack above the summary until both fit on one line. */}
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between xl:gap-8">
            {/* Keep the well to one row: it is a single container, so a second row
                renders as a mostly-empty band with one pill stranded in it. Too
                narrow to fit them all, it scrolls sideways instead. */}
            <div
              role="tablist"
              className="flex flex-nowrap gap-1 overflow-x-auto p-1 bg-gray-100 rounded-lg xl:shrink-0 xl:overflow-visible"
            >
              {FILTER_TABS.map((tab) => {
                const isSelected = tab.key === activeFilter;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setFilter(tab.key)}
                    className={`whitespace-nowrap rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-white text-gray-950 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {/* Yields width to the pills, not the reverse: only this block can
                reflow, and its width varies as "(N pending)" and the publisher's
                contact address come and go. */}
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
                <span className="font-medium text-gray-900 whitespace-nowrap">
                  {remaining} {remaining === 1 ? 'seat' : 'seats'} remaining
                  {/* See note above — hidden on mobile to dedupe with the Manage-plan dropdown. */}
                  {seatActionSlot}
                </span>
                <span className="text-right text-gray-500 whitespace-nowrap">
                  {group.seats_filled} of {group.max_seats} filled
                  {group.invites_pending > 0 && ` (${group.invites_pending} pending)`}
                </span>
              </div>
              <div className="relative w-full h-2 bg-gray-100 rounded-lg">
                <div
                  className="absolute top-0 left-0 h-2 rounded-lg bg-wt-primary"
                  style={{ width: `${percentFilled}%` }}
                />
              </div>
            </div>
          </div>

          <SeatSearchInput value={searchQuery} onChange={handleSearchChange} />

          {/* Below md the table collapses to email-with-stacked-metadata + actions —
              status/date columns hide and stack under the email in MemberRow. md+
              restores the full 4-column grid; min-w-[760px] then forces the natural
              floor so columns don't squeeze when the parent gets narrow (overflow-x
              scrolls when needed at that breakpoint). */}
          <div className="bg-white border border-gray-200 rounded-lg md:overflow-x-auto">
            <div className="md:min-w-[760px]">
              {hasSelection ? (
                <BulkActionBar
                  groupId={group.id}
                  selectedSeats={selectedSeats}
                  selectionCount={selectedIds.size}
                  clearSelection={clearSelection}
                  onBulkRemove={
                    onOpenConfirm
                      ? (_, ids) => onOpenConfirm('remove', ids, firstEmailOf(ids, selectedSeats))
                      : undefined
                  }
                  onBulkResendInvite={
                    onOpenConfirm
                      ? (_, ids) => onOpenConfirm('resend-invite', ids, firstEmailOf(ids, selectedSeats))
                      : undefined
                  }
                  onBulkCancelInvites={
                    onOpenConfirm
                      ? (_, ids) => onOpenConfirm('cancel-invite', ids, firstEmailOf(ids, selectedSeats))
                      : undefined
                  }
                  onBulkReinvite={
                    onOpenConfirm
                      ? (_, ids) => onOpenConfirm('reinvite', ids, firstEmailOf(ids, selectedSeats))
                      : undefined
                  }
                />
              ) : (
                <div className="flex h-[54px] items-center border-b border-gray-200 bg-gray-50">
                  {/* Email header grows on mobile (matching the collapsed row); reverts
                      to fixed 300px at md+ alongside the Status/Date headers. */}
                  <div className="flex h-full flex-1 items-center gap-3 px-4 py-3 md:w-[300px] md:flex-none">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      className={cn(
                        'w-4 h-4 border-gray-400 rounded shrink-0',
                        allVisibleSelected ? '!bg-wt-primary !text-wt-text-on-primary' : '',
                        !hasActionableVisible ? 'opacity-40 cursor-not-allowed' : '',
                      )}
                      checked={allVisibleSelected}
                      disabled={!hasActionableVisible}
                      onChange={toggleAllVisible}
                      aria-label="Select all visible seats"
                    />
                    <span className="text-xs font-medium text-gray-500 uppercase">Email</span>
                  </div>
                  <div className="hidden flex-1 items-center justify-center px-4 py-3 md:flex md:h-full">
                    <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
                  </div>
                  <div className="hidden flex-1 items-center px-4 py-3 md:flex md:h-full">
                    <span className="text-xs font-medium text-gray-500 uppercase">Date added</span>
                  </div>
                  <div className="hidden md:block md:h-full md:w-[206px] md:px-4 md:py-3" />
                </div>
              )}

              {isLoadingSeats && visibleSeats.length === 0 ? (
                <div className="flex h-16 items-center justify-center px-4 py-3 text-sm text-gray-500">Loading…</div>
              ) : visibleSeats.length === 0 ? (
                <div className="flex h-16 items-center justify-center px-4 py-3 text-sm text-gray-500">
                  {isSearching ? 'No members match your search.' : 'No seats match this filter.'}
                </div>
              ) : (
                visibleSeats.map((seat) => (
                  <MemberRow
                    key={seat.id}
                    seat={seat}
                    isSelected={selectedIds.has(seat.id)}
                    onToggle={toggleSeat}
                    translations={translations}
                    rowCallbacks={{
                      onResendInvite: onOpenConfirm
                        ? () => onOpenConfirm('resend-invite', [seat.id], seat.email)
                        : undefined,
                      onCancelInvite: onOpenConfirm
                        ? () => onOpenConfirm('cancel-invite', [seat.id], seat.email)
                        : undefined,
                      onReinvite: onOpenConfirm ? () => onOpenConfirm('reinvite', [seat.id], seat.email) : undefined,
                      onRemove: onOpenConfirm ? () => onOpenConfirm('remove', [seat.id], seat.email) : undefined,
                      onManageReduction: seatCountLocked ? undefined : onModifyReduction,
                    }}
                  />
                ))
              )}
            </div>
          </div>

          {pagination && pagination.total > pagination.per_page && (
            <SeatPagination
              pagination={pagination}
              isLoading={isLoadingSeats}
              onPageChange={(next: number) => onPageChange?.(next)}
            />
          )}
        </>
      )}
    </div>
  );
}
